import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, withKeys as _withKeys, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "grid grid-cols-3 gap-1" }
const _hoisted_2 = { class: "absolute right-2 flex size-3.5 items-center justify-center" }
const _hoisted_3 = { class: "text-muted-foreground p-2 text-xs" }

import { ChevronDown, Check, X, LoaderCircle } from 'lucide-vue-next';
    import {
        ComboboxAnchor,
        ComboboxContent,
        ComboboxInput,
        ComboboxItem,
        ComboboxLabel,
        ComboboxGroup,
        ComboboxEmpty,
        ComboboxRoot,
        ComboboxTrigger,
        ComboboxViewport,
        ComboboxSeparator,
        ComboboxItemIndicator,
        TagsInputInput,
        TagsInputItem,
        TagsInputItemDelete,
        TagsInputItemText,
        TagsInputRoot,
        useFilter,
    } from 'reka-ui';
    import { computed, ref } from 'vue';
    import type { RoleGroup, RoleOption } from '@/types';
    import { useI18n } from 'vue-i18n';

    interface Props {
        modelValue: RoleOption[];
        options: RoleGroup[];
        placeholder?: string;
        translate?: boolean;
        loading?: boolean;
        grouped?: boolean;
    }

    
export default /*@__PURE__*/_defineComponent({
  __name: 'UserRoleComboBox',
  props: {
    modelValue: {},
    options: {},
    placeholder: { default: '' },
    translate: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    grouped: { type: Boolean, default: false }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

    const props = __props;

    const emit = __emit;

    const { t } = useI18n();
    const { contains } = useFilter({ sensitivity: 'base' });

    const searchTerm = ref('');
    const open = ref(false);
    const comboboxInputRef = ref<InstanceType<typeof ComboboxInput> | null>(null);

    const values = computed({
        get: () => props.modelValue,
        set: (newValues) => {
            emit('update:modelValue', newValues);
        },
    });

    const placeholderWithDefault = computed(
        () => props.placeholder || t('forms.select_placeholder'),
    );

    const filteredOptions = computed(() =>
        props.options
            .filter((group) => group.roles.some((role) => contains(role.name, searchTerm.value)))
            .map((group) => ({
                ...group,
                roles: group.roles.filter((role) => contains(role.name, searchTerm.value)),
            })),
    );

    const selectedRolesByCompany = computed(() => {
        const selectedMap = new Map<number, RoleOption>();
        values.value.forEach((role) => {
            selectedMap.set(role.company_id, role);
        });
        return selectedMap;
    });

    const isRoleDisabled = (role: RoleOption) => {
        return (
            selectedRolesByCompany.value.has(role.company_id) &&
            selectedRolesByCompany.value.get(role.company_id)?.id !== role.id
        );
    };

    const disabledStates = computed(() => {
        const disabledMap = new Map<RoleOption, boolean>();

        filteredOptions.value.forEach((group) => {
            group.roles.forEach((role) => {
                disabledMap.set(role, isRoleDisabled(role));
            });
        });

        return disabledMap;
    });

    const handleKeydown = (event: KeyboardEvent) => {
        if (event.key === 'Tab') {
            open.value = false;
        }
    };

    const handleInteractOutside = (event: any) => {
        if (event.target !== comboboxInputRef.value?.$el) {
            open.value = false;
        }
    };

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(ComboboxRoot), {
    modelValue: values.value,
    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((values).value = $event)),
    open: open.value,
    "onUpdate:open": _cache[5] || (_cache[5] = ($event: any) => ((open).value = $event)),
    multiple: true,
    "ignore-filter": "",
    class: "relative"
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(ComboboxAnchor), { class: "text-foreground data-[placeholder]:text-faded-foreground border-input flex min-h-7.5 w-full items-center justify-between gap-[5px] rounded-md border bg-white p-0.75 text-xs leading-none outline-none" }, {
        default: _withCtx(() => [
          _createVNode(_unref(TagsInputRoot), {
            modelValue: values.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((values).value = $event)),
            delimiter: "",
            class: "flex flex-1 flex-wrap items-center gap-1 rounded-lg"
          }, {
            default: _withCtx(() => [
              (!_ctx.loading)
                ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(values.value, (value) => {
                    return (_openBlock(), _createBlock(_unref(TagsInputItem), {
                      key: value.id,
                      value: value,
                      class: "bg-primary/10 aria-[current=true]:bg-primary text-foreground border-primary/20 flex items-center justify-center gap-2 rounded border px-1.5 py-0.5"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(TagsInputItemText), { class: "text-xs" }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(value.name), 1)
                          ]),
                          _: 2
                        }, 1024),
                        _createVNode(_unref(TagsInputItemDelete), null, {
                          default: _withCtx(() => [
                            _createVNode(_unref(X), { class: "size-3 cursor-pointer" })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 2
                    }, 1032, ["value"]))
                  }), 128))
                : _createCommentVNode("", true),
              _createVNode(_unref(ComboboxInput), {
                ref_key: "comboboxInputRef",
                ref: comboboxInputRef,
                "as-child": "",
                modelValue: searchTerm.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchTerm).value = $event)),
                onFocus: _cache[2] || (_cache[2] = 
                        () => {
                            open.value = true;
                        }
                    )
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(TagsInputInput), {
                    placeholder: placeholderWithDefault.value,
                    class: "placeholder:text-faded-foreground h-5.5 flex-1 rounded !bg-transparent pl-1.75 focus:outline-none",
                    onKeydown: [
                      _cache[0] || (_cache[0] = _withKeys(_withModifiers(() => {}, ["prevent"]), ["enter"])),
                      handleKeydown
                    ]
                  }, null, 8, ["placeholder"])
                ]),
                _: 1
              }, 8, ["modelValue"])
            ]),
            _: 1
          }, 8, ["modelValue"]),
          _createVNode(_unref(ComboboxTrigger), { class: "shrink-0 pr-1.75" }, {
            default: _withCtx(() => [
              (!_ctx.loading)
                ? (_openBlock(), _createBlock(_unref(ChevronDown), {
                    key: 0,
                    class: "text-foreground/50 size-4 cursor-pointer"
                  }))
                : (_openBlock(), _createBlock(_unref(LoaderCircle), {
                    key: 1,
                    class: "text-foreground/50 ml-auto size-4 animate-spin"
                  }))
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_unref(ComboboxContent), {
        class: "data-[state=open]:animate-in data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[50% 0px] absolute z-10 mt-1 max-h-[200px] w-[var(--reka-popper-anchor-width)] overflow-y-auto rounded-md border bg-white p-1 shadow-md will-change-[opacity,transform]",
        onPointerDownOutside: _withModifiers(handleInteractOutside, ["prevent"]),
        position: "popper",
        align: "start"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(ComboboxViewport), null, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredOptions.value, (group, index) => {
                return (_openBlock(), _createBlock(_unref(ComboboxGroup), {
                  key: group.name,
                  class: "my-2 flex flex-col gap-2 first:mt-0"
                }, {
                  default: _withCtx(() => [
                    (index !== 0)
                      ? (_openBlock(), _createBlock(_unref(ComboboxSeparator), {
                          key: 0,
                          class: "bg-border m-2 h-[1px]"
                        }))
                      : _createCommentVNode("", true),
                    _createVNode(_unref(ComboboxLabel), { class: "px-2 text-xs font-bold" }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(group.name), 1)
                      ]),
                      _: 2
                    }, 1024),
                    _createElementVNode("div", _hoisted_1, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.roles, (option) => {
                        return (_openBlock(), _createBlock(_unref(ComboboxItem), {
                          key: `${option.id}-${disabledStates.value.get(option)}`,
                          class: "data-[state=checked]:bg-primary/10 text-foreground data-[disabled]:text-faded-foreground data-[highlighted]:text-primary relative flex w-full cursor-pointer items-center gap-2 rounded-sm py-1.5 pr-2 pl-2 text-xs leading-none select-none data-[disabled]:pointer-events-none data-[highlighted]:outline-none data-[state=checked]:pr-8",
                          value: option,
                          disabled: disabledStates.value.get(option)
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("span", null, _toDisplayString(option.name), 1),
                            _createElementVNode("span", _hoisted_2, [
                              _createVNode(_unref(ComboboxItemIndicator), null, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(Check), { class: "text-primary size-3" })
                                ]),
                                _: 1
                              })
                            ])
                          ]),
                          _: 2
                        }, 1032, ["value", "disabled"]))
                      }), 128))
                    ])
                  ]),
                  _: 2
                }, 1024))
              }), 128)),
              _createVNode(_unref(ComboboxEmpty), null, {
                default: _withCtx(() => [
                  _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(t)('forms.no_options')), 1)
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue", "open"]))
}
}

})